import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { fitLifted, settle } from '../../chrome';
import { TextField } from '../../components';
import { useT } from '../../i18n';
import { ApiError, isRateLimited, useAirportSearch, type Airport } from '../../lib/api';
import { flagEmoji } from '../../lib/format';

/** "Barcelona (BCN)": what the input shows once an airport is picked (the prototype's form). */
const airportText = (a: Airport | null) => (a ? `${a.city} (${a.iata})` : '');

/**
 * The search failed (after the query's own retries): not "no airport matches" (w4-01 fix 9).
 * The API or the edge answered (5xx, 503 while it restarts) → our side; no answer → the
 * connection; 429 → wait.
 */
function searchErrorKey(e: unknown) {
  if (isRateLimited(e)) return 'common.rateLimited' as const;
  return e instanceof ApiError ? ('S1.searchDown' as const) : ('S1.searchOffline' as const);
}

/** Wait this long after the last keystroke before asking the API (our airports table). */
const DEBOUNCE_MS = 120;

function useDebounced(value: string) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);
  return v;
}

export interface AirportFieldProps {
  label: ReactNode;
  placeholder: string;
  value: Airport | null;
  onPick: (a: Airport) => void;
  /** Typing again after a pick clears the value. */
  onClear: () => void;
  /**
   * A pick with Enter (keyboard only): where focus goes next. Without it focus stays in the
   * input. A tap or click still blurs, so a phone's keyboard closes (w6-04 N6).
   */
  onKeyboardPick?: (input: HTMLInputElement) => void;
  /** A control inside the input on the right (a connection's ✕). */
  trailing?: ReactNode;
  error?: ReactNode;
  autoFocus?: boolean;
}

/**
 * S1's airport input (uncontrolled text: remount it with a new `key` to show a value set elsewhere): suggestions from `GET /airports` as you type (city + IATA, formal name
 * beneath, flag at the right), arrow keys + Enter, Escape closes. A failed search says so,
 * apart from "no airport matches", with "Try again" (or Enter). Focus stays in the input
 * while a row is pressed, as in the prototype, so the lifted page doesn't jump. A pick with
 * Enter hands focus on (`onKeyboardPick`); a tap blurs, which closes a phone's keyboard.
 */
export function AirportField({
  label,
  placeholder,
  value,
  onPick,
  onClear,
  onKeyboardPick,
  trailing,
  error,
  autoFocus,
}: AirportFieldProps) {
  const t = useT();
  const [text, setText] = useState(airportText(value));
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const q = useDebounced(value ? '' : text);
  const search = useAirportSearch(q);
  const rows = search.data ?? [];

  // The list grew or shrank: keep it above the keyboard.
  useEffect(() => {
    if (open) fitLifted();
  }, [open, rows.length]);

  /** `input`: picked with Enter from this input; focus moves on rather than falling to <body>. */
  function pick(a: Airport, input?: HTMLInputElement) {
    setText(airportText(a));
    setOpen(false);
    setCursor(0);
    if (!input) {
      (document.activeElement as HTMLElement | null)?.blur();
      settle();
    }
    onPick(a);
    if (input) onKeyboardPick?.(input);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (open && failed && e.key === 'Enter') {
      e.preventDefault();
      void search.refetch();
      return;
    }
    if (!open || !rows.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (e.key === 'ArrowDown' ? Math.min(c + 1, rows.length - 1) : Math.max(c - 1, 0)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(rows[Math.min(cursor, rows.length - 1)]!, e.currentTarget);
    } else if (e.key === 'Escape') setOpen(false);
  }

  const typed = !value && text.trim().length > 0;
  const settled = typed && q === text && !search.isFetching;
  const failed = settled && search.isError;
  const listOpen = open && typed && (rows.length > 0 || settled);

  return (
    <TextField
      label={label}
      icon="plane"
      filled={!!value}
      placeholder={placeholder}
      value={text}
      autoComplete="off"
      spellCheck={false}
      autoFocus={autoFocus}
      role="combobox"
      aria-expanded={listOpen}
      aria-autocomplete="list"
      error={error}
      trailing={trailing}
      onChange={(e) => {
        setText(e.target.value);
        setOpen(true);
        setCursor(0);
        if (value) onClear();
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={onKeyDown}
    >
      {listOpen && (
        <div className="ac" role="listbox" data-lift-extra onMouseDown={(e) => e.preventDefault()}>
          {rows.length ? (
            rows.map((a, i) => (
              <button
                key={a.iata}
                type="button"
                role="option"
                aria-selected={i === cursor}
                // The visible city and code run together ("BarcelonaBCN") for a screen reader.
                aria-label={`${a.city} (${a.iata}), ${a.name}`}
                className={`ac__row${i === cursor ? ' is-cursor' : ''}`}
                onClick={() => pick(a)}
              >
                <span>
                  <span className="ac__city">
                    {a.city}
                    <b>{a.iata}</b>
                  </span>
                  <span className="ac__name">{a.name}</span>
                </span>
                <span className="ac__flag" aria-hidden="true">
                  {flagEmoji(a.countryCode)}
                </span>
              </button>
            ))
          ) : failed ? (
            <div className="ac__empty ac__empty--error" role="alert">
              <span>{t(searchErrorKey(search.error))}</span>
              <button type="button" className="ac__retry" onClick={() => void search.refetch()}>
                {t('S1.searchRetry')}
              </button>
            </div>
          ) : (
            <div className="ac__empty">{t('S1.noMatch')}</div>
          )}
        </div>
      )}
    </TextField>
  );
}
